import { useState, useRef, useEffect, ReactNode } from "react";

interface SheetProps {
  state: "closed" | "spices" | "info";
  onClose?: () => void;
  className?: string;
  children?: ReactNode;
}

export function Sheet({
  state,
  onClose,
  className = "",
  children,
}: SheetProps) {
  const isOpen = state !== "closed";
  const [isExpanded, setIsExpanded] = useState(false);

  const dragStartY = useRef<number | null>(null);

  // Reset to half-open whenever sheet opens
  useEffect(() => {
    if (isOpen) {
      setIsExpanded(false);
    }
  }, [isOpen]);

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientY = "touches" in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    dragStartY.current = clientY;
  };

  const handleDragEnd = (e: React.TouchEvent | React.MouseEvent) => {
    if (dragStartY.current === null) return;
    const clientY =
      "changedTouches" in e
        ? e.changedTouches[0].clientY
        : (e as React.MouseEvent).clientY;
    const delta = clientY - dragStartY.current;

    if (delta > 60) {
      if (isExpanded) {
        setIsExpanded(false);
      } else {
        onClose?.();
      }
    } else if (delta < -60) {
      setIsExpanded(true);
    }
    dragStartY.current = null;
  };

  return (
    <div
      className={`backdrop-blur-lg  rounded-t-xl shadow-lg flex flex-col lg:hidden w-full overflow-hidden transition-[height] duration-300 ease-out ${
        !isOpen ? "h-0 pointer-events-none" : isExpanded ? "h-[65%] pointer-events-auto border-t border-secondary/20" : "h-[50%] pointer-events-auto border-t border-secondary/20"
      } ${className}`}
    >
      {/* Drag Handle */}
      <div
        className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing flex-shrink-0"
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
      >
        <div className="w-12 h-1 bg-secondary rounded-full" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-3 flex flex-col justify-start">
        {children}
      </div>
    </div>
  );
}
